import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { api, type ApiResponse } from "@/api/client";
import { studioKeys, type StudioCampaign } from "./studioTypes";

/**
 * NL Studio "Results" tab (block 4.15 řez 4) — sent-campaign funnel table with expandable
 * click breakdowns + the cross-campaign "audience interest" destination rollup.
 */

interface ClickStats {
  links: Array<{
    url: string;
    label: string;
    kind: "trip" | "destination" | "page" | "system";
    section: string | null;
    clicks: number;
  }>;
  systemClicks: number;
  sections: Array<{ section: string; clicks: number }>;
  destinations: Array<{ name: string; clicks: number }>;
}

type CampaignStats = ClickStats & {
  funnel: { total: number; sent: number; failed: number; opened: number; clicked: number };
};

export default function ResultsPanel() {
  const { data } = useQuery({
    queryKey: studioKeys.results,
    queryFn: () => api.get<ApiResponse<StudioCampaign[]>>("/api/admin/newsletter/campaigns"),
  });
  const [retroFor, setRetroFor] = useState<string | null>(null);
  const [retroNote, setRetroNote] = useState("");
  const [openStats, setOpenStats] = useState<string | null>(null);

  const sent = (data?.data ?? []).filter((c) => c.status === "sent");

  const saveRetro = async (campaignId: string) => {
    if (!retroNote.trim()) return;
    try {
      await api.post("/api/admin/newsletter-studio/feedback", {
        scope: "campaign",
        refId: campaignId,
        note: retroNote,
      });
      toast.success("Retro note saved — the analyst will see it");
      setRetroFor(null);
      setRetroNote("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save the note");
    }
  };

  return (
    <div className="space-y-5">
      <AudienceInterestCard />
      <div className="card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
              <th className="py-2">Campaign</th>
              <th>Sent</th>
              <th>Opens</th>
              <th>Clicks</th>
              <th>CTR</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sent.map((c) => (
              <Fragment key={c.id}>
                <tr>
                  <td className="max-w-md truncate py-2.5 font-medium text-gray-800">
                    <button
                      onClick={() => setOpenStats(openStats === c.id ? null : c.id)}
                      className="hover:text-primary-700 flex items-center gap-1 text-left"
                      title="Show click breakdown"
                    >
                      <ChevronRight
                        className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${openStats === c.id ? "rotate-90" : ""}`}
                      />
                      <span className="truncate">{c.subject}</span>
                    </button>
                  </td>
                  <td>{c.sentCount}</td>
                  <td>{c.openedCount}</td>
                  <td>{c.clickedCount}</td>
                  <td>
                    {c.sentCount > 0 ? `${Math.round((c.clickedCount / c.sentCount) * 100)}%` : "—"}
                  </td>
                  <td className="text-right">
                    {retroFor === c.id ? (
                      <span className="flex items-center gap-1">
                        <input
                          type="text"
                          value={retroNote}
                          onChange={(e) => setRetroNote(e.target.value)}
                          placeholder="What did we learn?"
                          className="w-56 rounded border border-gray-200 px-2 py-1 text-xs"
                          autoFocus
                        />
                        <button
                          onClick={() => saveRetro(c.id)}
                          className="btn-primary !px-2 !py-1 !text-xs"
                        >
                          Save
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => {
                          // Reset the draft text — a note typed for another campaign must not
                          // silently carry over and get saved against this one.
                          setRetroFor(c.id);
                          setRetroNote("");
                        }}
                        className="text-primary-600 text-xs hover:underline"
                      >
                        + retro note
                      </button>
                    )}
                  </td>
                </tr>
                {openStats === c.id && (
                  <tr>
                    <td colSpan={6} className="bg-gray-50/60 px-3 py-3">
                      <CampaignStatsDetail campaignId={c.id} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {!sent.length && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-gray-400">
                  No sent campaigns yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Horizontal mini bar list (label + count) used for destinations/sections. */
function BarList({ items }: { items: Array<{ label: string; value: number }> }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="space-y-1.5">
      {/* labels are not unique (query-stripped URLs, shared trip titles) — key by index */}
      {items.map((i, idx) => (
        <div key={idx} className="flex items-center gap-2 text-xs">
          <span className="w-36 truncate text-gray-600" title={i.label}>
            {i.label}
          </span>
          <div className="h-3 flex-1 overflow-hidden rounded bg-gray-100">
            <div
              className="bg-primary-500 h-full rounded"
              style={{ width: `${Math.round((i.value / max) * 100)}%` }}
            />
          </div>
          <span className="w-8 text-right font-medium text-gray-700">{i.value}</span>
        </div>
      ))}
      {!items.length && <p className="text-xs text-gray-400">No clicks yet.</p>}
    </div>
  );
}

/** Cross-campaign "what is the audience interested in" rollup (destinations by clicks). */
function AudienceInterestCard() {
  const { data } = useQuery({
    queryKey: ["studio-stats-overview"],
    queryFn: () => api.get<ApiResponse<ClickStats>>("/api/admin/newsletter-studio/stats/overview"),
    staleTime: 60_000,
  });
  const stats = data?.data;
  if (!stats || (!stats.destinations.length && !stats.links.length)) return null;
  return (
    <div className="card">
      <h3 className="mb-3 text-sm font-semibold text-gray-700">
        Audience interest — clicks by destination (all campaigns)
      </h3>
      <div className="grid gap-6 md:grid-cols-2">
        <BarList items={stats.destinations.map((d) => ({ label: d.name, value: d.clicks }))} />
        <div>
          <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">
            Most clicked links
          </p>
          <BarList
            items={stats.links.slice(0, 6).map((l) => ({ label: l.label, value: l.clicks }))}
          />
        </div>
      </div>
    </div>
  );
}

/** Expandable per-campaign breakdown: funnel + labeled links + sections + destinations. */
function CampaignStatsDetail({ campaignId }: { campaignId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["studio-campaign-stats", campaignId],
    queryFn: () =>
      api.get<ApiResponse<CampaignStats>>(
        `/api/admin/newsletter-studio/campaigns/${campaignId}/stats`,
      ),
  });
  if (isLoading || !data?.data) {
    return <Loader2 className="mx-auto h-5 w-5 animate-spin text-gray-300" />;
  }
  const s = data.data;
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-600">
        Delivered <strong>{s.funnel.sent}</strong>/{s.funnel.total}
        {s.funnel.failed > 0 && <span className="text-red-600"> · {s.funnel.failed} failed</span>}
        {" · "}
        unique opens <strong>{s.funnel.opened}</strong> · unique clickers{" "}
        <strong>{s.funnel.clicked}</strong>
        {s.systemClicks > 0 && ` · ${s.systemClicks} housekeeping clicks (unsub/view)`}
      </p>
      <div className="grid gap-6 md:grid-cols-3">
        <div>
          <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">
            Top links
          </p>
          <BarList items={s.links.map((l) => ({ label: l.label, value: l.clicks }))} />
        </div>
        <div>
          <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">
            By section
          </p>
          <BarList items={s.sections.map((x) => ({ label: x.section, value: x.clicks }))} />
        </div>
        <div>
          <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">
            By destination
          </p>
          <BarList items={s.destinations.map((d) => ({ label: d.name, value: d.clicks }))} />
        </div>
      </div>
    </div>
  );
}
